import { Icon } from '@design-system';
import { useTranslation } from 'next-i18next';
import React, { FC, ReactNode, useCallback, useMemo } from 'react';

import { GeneratedDelegateCashOutput } from '../../../types/generated';
import { concatWeb3Address } from '../../../utils/web3/utils';
import {
  SimpleDropDownMenu,
  SimpleDropDownMenuItem,
} from '../../SimpleDropDownMenu/SimpleDropDownMenu';
import { useWeb3 } from '../Web3Provider/useWeb3';

interface WalletDropdownProps {
  delegates?: GeneratedDelegateCashOutput[];
  activeDelegate?: GeneratedDelegateCashOutput;
  onLogout?: () => void;
  onMainWalletSelected?: () => void;
  onDelegateSelected?: (delegate: GeneratedDelegateCashOutput) => void;
}

interface WalletListItemProps {
  address: string;
  icon: ReactNode;
}

const WalletListItem: FC<WalletListItemProps> = ({ address, icon }) => {
  return (
    <div className="flex min-w-0 items-center">
      {icon}
      <span className="truncate pl-2">{concatWeb3Address(address)}</span>
    </div>
  );
};

export const WalletDropdown: FC<WalletDropdownProps> = ({
  activeDelegate,
  delegates,
  onDelegateSelected,
  onLogout,
  onMainWalletSelected,
}) => {
  const { logout, walletAddress } = useWeb3();
  const { t } = useTranslation('web3Product');

  const handleLogout = useCallback(() => {
    logout();
    if (onLogout) {
      onLogout();
    }
  }, [onLogout, logout]);

  const dropdownItems = useMemo(() => {
    let items: SimpleDropDownMenuItem[] = [];

    if (walletAddress && delegates && delegates.length > 0) {
      items = [
        {
          onClick: () => onMainWalletSelected && onMainWalletSelected(),
          node: (
            <WalletListItem
              address={walletAddress}
              icon={<Icon name="ic_wallet" size="md" />}
            />
          ),
        },
        ...delegates.map((delegate) => ({
          onClick: () => onDelegateSelected && onDelegateSelected(delegate),
          node: (
            <WalletListItem
              address={delegate.vault}
              icon={<Icon name="ic_delegatecash" size="md" />}
            />
          ),
        })),
      ];
    }

    return [
      ...items,
      {
        onClick: () => handleLogout(),
        node: t('disconnectWallet'),
      },
    ];
  }, [
    delegates,
    handleLogout,
    t,
    onDelegateSelected,
    onMainWalletSelected,
    walletAddress,
  ]);

  if (!walletAddress) {
    return null;
  }

  return (
    <SimpleDropDownMenu items={dropdownItems} lastItemDivider>
      {activeDelegate ? (
        <WalletListItem
          address={activeDelegate.vault}
          icon={<Icon name="ic_delegatecash" size="md" />}
        />
      ) : (
        <WalletListItem
          address={walletAddress}
          icon={<Icon name="ic_wallet" size="md" />}
        />
      )}
    </SimpleDropDownMenu>
  );
};
